import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface SessionMetadata {
  source?: string;
  status?: string;
  applicationId?: string;
  clonedFrom?: string;
  /** Last user query written by the harness — used as list topic when history is not loaded. */
  lastQuery?: string;
  mcpGrantId?: string;
  mcpClient?: string;
}

export interface SessionListItem {
  sessionId: string;
  agentName: string;
  userId: string;
  orgId?: string;
  createdAt?: string;
  updatedAt?: string;
  metadata?: SessionMetadata;
}

export interface SessionMessage {
  role: 'user' | 'assistant' | 'system' | string;
  content: string;
  timestamp?: string | null;
}

export interface SessionsListResponse {
  sessions: SessionListItem[];
}

export interface SessionHistoryResponse {
  sessionId: string;
  agentName: string;
  orgId?: string;
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
  metadata?: SessionMetadata;
  messages: SessionMessage[];
}

export interface CloneSessionResponse {
  sessionId: string;
  agentName: string;
  messageCount: number;
  clonedFrom: string;
}

export const sessionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSessions: builder.query<SessionsListResponse, { agentName?: string } | void>({
      query: (args) => {
        const agentName = args && 'agentName' in args ? args.agentName : undefined;
        const params = new URLSearchParams();
        if (agentName) params.set('agent-name', agentName);
        const qs = params.toString();
        return `/sessions${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: unknown) =>
        normalizeKeys<SessionsListResponse>(response),
      providesTags: (result) =>
        result
          ? [
              ...result.sessions.map(({ sessionId }) => ({
                type: 'Session' as const,
                id: sessionId,
              })),
              { type: 'Session', id: 'LIST' },
            ]
          : [{ type: 'Session', id: 'LIST' }],
    }),

    getSessionHistory: builder.query<SessionHistoryResponse, string>({
      query: (sessionId) => `/sessions/${sessionId}/history`,
      transformResponse: (response: unknown) =>
        normalizeKeys<SessionHistoryResponse>(response),
      providesTags: (_result, _error, id) => [{ type: 'Session', id }],
    }),

    cloneSession: builder.mutation<CloneSessionResponse, string>({
      query: (sessionId) => ({
        url: `/sessions/${sessionId}/clone`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) =>
        normalizeKeys<CloneSessionResponse>(response),
      invalidatesTags: [{ type: 'Session', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSessionsQuery,
  useGetSessionHistoryQuery,
  useLazyGetSessionHistoryQuery,
  useCloneSessionMutation,
} = sessionsApi;
